// ============================================================================
// TeamsTab — onglet EQUIPES de l'ecran Admin (AD-B)
// ============================================================================
// Porte du PoC components/admin/teams-section.tsx aux standards du parc : la
// grille de cartes PoC devient une DataTable @gs/ui (releve Fable — les tables
// brutes du PoC deviennent DataTable), creation/edition via TeamFormDialog
// (GsWizardDialog AD-0). Le clic de ligne OUVRE l'edition (drill-in standard #2
// — ici l'action attendue de l'admin). Erreur mutation -> alerte i18n locale
// (lecon GA-21 : jamais de code brut a l'ecran).
// Le pool assignable vient du bundle serveur (ce/cg + directors actuels) —
// STOP-8 : l'affectation est decouplee du role UCBR.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Plus, Users } from 'lucide-react';
import { DataTable, EmptyState, SkeletonCard, QueryError, GS_COMPONENTS, type Column } from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useCreateTeam, useUpdateTeam, adminTeamErrorCode,
  type AdminTeamsResponse, type AdminTeamSummary, type TeamUpdatePayload,
} from '../../hooks/useAdminTeams';
import { SectionHeader } from './SectionHeader';
import { TeamFormDialog, type TeamFormValues } from './TeamFormDialog';

// `initial` est FIGE au moment du clic (objet stable) : le passer par l'etat
// evite qu'un re-render re-seed le formulaire pendant la saisie (le useEffect du
// dialog depend de l'identite de `initial`).
type DialogState =
  | { mode: 'create' }
  | { mode: 'edit'; team: AdminTeamSummary; initial: TeamFormValues }
  | null;

function initialValuesFor(team: AdminTeamSummary): TeamFormValues {
  return {
    name: team.name,
    department: team.department ?? '',
    headcount: String(team.headcount),
    directorEmail: team.directorEmail ?? '',
  };
}

export function TeamsTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminTeamsResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.teams.loadError')} onRetry={onRetry} />;
  }

  const pending = createTeam.isPending || updateTeam.isPending;

  function submit(payload: TeamUpdatePayload) {
    setErrorCode(null);
    const done = {
      onSuccess: () => setDialog(null),
      onError: (err: unknown) => {
        setDialog(null);
        setErrorCode(adminTeamErrorCode(err));
      },
    };
    if (dialog?.mode === 'edit') {
      updateTeam.mutate({ id: dialog.team.id, payload }, done);
    } else {
      // Creation : director NULL a la creation (parite PoC) — payload reduit.
      createTeam.mutate(
        { name: payload.name, department: payload.department, headcount: payload.headcount },
        done,
      );
    }
  }

  const columns: Column<AdminTeamSummary>[] = [
    {
      key: 'name',
      header: t('gest:admin.teams.columns.name'),
      render: (team) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-900 dark:text-slate-100">{team.name}</p>
          {team.department ? (
            <p className="truncate text-meta text-slate-500 dark:text-slate-400">{team.department}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'director',
      header: t('gest:admin.teams.columns.director'),
      render: (team) =>
        team.directorName ?? team.directorEmail ?? (
          <span className="text-slate-400 dark:text-slate-500">{t('gest:admin.teams.noDirector')}</span>
        ),
    },
    {
      key: 'members',
      header: t('gest:admin.teams.columns.members'),
      className: 'tabular-nums',
      render: (team) => team.memberCount || '—',
    },
    {
      key: 'headcount',
      header: t('gest:admin.teams.columns.headcount'),
      className: 'tabular-nums',
      render: (team) => team.headcount || '—',
    },
    {
      key: 'actions',
      header: t('gest:admin.teams.columns.actions'),
      className: 'text-right',
      render: (team) => (
        <button
          type="button"
          aria-label={t('gest:admin.teams.editMeta', { name: team.name })}
          onClick={(e) => {
            e.stopPropagation();
            setErrorCode(null);
            setDialog({ mode: 'edit', team, initial: initialValuesFor(team) });
          }}
          className="inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <Pencil size={16} aria-hidden="true" />
        </button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.teams.title')}
        description={t('gest:admin.teams.description')}
      >
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.teams.count', { n: data.teams.length })}
        </span>
        <button
          type="button"
          onClick={() => { setErrorCode(null); setDialog({ mode: 'create' }); }}
          className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
        >
          <Plus size={14} aria-hidden="true" />
          {t('gest:admin.teams.create')}
        </button>
      </SectionHeader>

      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.teams.errors.${errorCode}`)}
        </p>
      ) : null}

      {data.teams.length === 0 ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:admin.teams.empty')}
          description={t('gest:admin.teams.emptyHint')}
        />
      ) : (
        <div className={`${GS_COMPONENTS.card} overflow-hidden p-0`}>
          <DataTable
            columns={columns}
            data={data.teams}
            rowKey={(team) => team.id}
            onRowClick={(team) => {
              setErrorCode(null);
              navigate(`/admin/teams/${team.id}`);
            }}
          />
        </div>
      )}

      <TeamFormDialog
        open={dialog != null}
        mode={dialog?.mode ?? 'create'}
        initial={dialog?.mode === 'edit' ? dialog.initial : undefined}
        assignable={data.assignable}
        isPending={pending}
        onClose={() => setDialog(null)}
        onSubmit={submit}
      />
    </div>
  );
}
